import { flattenLegalInlines } from "@/legal/legalDocument";

/**
 * Busca local sobre o documento legal estruturado.
 * Não indexa JSX e não depende de backend, CMS ou IA.
 */

/**
 * @param {string} value
 * @returns {string}
 */
export function normalizeLegalQuery(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * @param {import("@/legal/legalDocument").LegalSectionContent} section
 * @returns {string}
 */
export function getLegalSectionSearchText(section) {
  if (!section) {
    return "";
  }

  const blocks = (section.blocks ?? [])
    .map((block) => {
      if (block.type === "list") {
        return (block.items ?? [])
          .map((item) => flattenLegalInlines(item))
          .join("\n");
      }

      return flattenLegalInlines(block.content);
    })
    .join("\n");

  const keywords = Array.isArray(section.keywords)
    ? section.keywords.join(" ")
    : "";

  return [section.title, section.label, keywords, blocks]
    .filter(Boolean)
    .join("\n");
}

/**
 * @param {string} text
 * @param {string} query
 * @param {number} [radius]
 * @returns {string}
 */
export function extractLegalSnippet(text, query, radius = 72) {
  const source = String(text ?? "").replace(/\s+/g, " ").trim();
  const normalizedSource = normalizeLegalQuery(source);
  const normalizedQuery = normalizeLegalQuery(query);

  if (!source) {
    return "";
  }

  if (!normalizedQuery) {
    return source.slice(0, radius * 2);
  }

  const index = normalizedSource.indexOf(normalizedQuery);
  if (index < 0) {
    return source.slice(0, radius * 2);
  }

  const start = Math.max(0, index - radius);
  const end = Math.min(source.length, index + normalizedQuery.length + radius);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < source.length ? "…" : "";

  return `${prefix}${source.slice(start, end).trim()}${suffix}`;
}

/**
 * @typedef {{
 *   id: string,
 *   title: string,
 *   label: string,
 *   snippet: string,
 * }} LegalSearchResult
 */

/**
 * @param {string} query
 * @param {import("@/legal/legalDocument").LegalDocumentContent | null | undefined} document
 * @returns {LegalSearchResult[]}
 */
export function searchLegalSections(query, document) {
  const normalized = normalizeLegalQuery(query);

  if (!normalized || !document?.sections?.length) {
    return [];
  }

  return document.sections
    .map((section) => {
      const text = getLegalSectionSearchText(section);
      if (!normalizeLegalQuery(text).includes(normalized)) {
        return null;
      }

      return {
        id: section.id,
        title: section.title,
        label: section.label || section.title,
        snippet: extractLegalSnippet(text, query),
      };
    })
    .filter(Boolean);
}
